import React, { useContext, useState } from 'react';
import { Dimmer, Icon, List, Loader, TextArea, TextAreaProps, Grid, Button } from 'semantic-ui-react';
import moment from 'moment';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import type { SweetAlertOptions } from 'sweetalert2';
import AppContext from '../../AppContext';
import axiosInstance from '../../helpers/axiosInstance';

interface CaseNoteProps {
  caseRecordId: number;
  caseNote: any;
  noteIndex: number;
}

type NoteFormErrorFields = {
  notes: string;
}

const CaseNote: React.FC<CaseNoteProps> = ({ caseRecordId, caseNote, noteIndex }) => {
  const { userInfo } = useContext(AppContext);

  const [notes, setNotes] = useState<string>('');
  const [caseNoteId, setCaseNoteId] = useState<number>(0);
  const [note, setNote] = useState<any>(caseNote);
  const [isShowEdit, setIsShowEdit] = useState<boolean>(false);
  const [isNoteLoading, setIsNoteLoading] = useState<boolean>(false);
  const [noteFormErrors, setNoteFormErrors] = useState<NoteFormErrorFields>({
    notes: ''
  });

  const handleTextAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>, data: TextAreaProps) => {
    const { value } = event.target;
    setNotes(value);
  };

  const validateNote = async (): Promise<NoteFormErrorFields> => {
    return await new Promise((resolve, reject) => {
      let errors: NoteFormErrorFields = { ...noteFormErrors };
      try {
        errors = {
          notes: !notes ? 'Required' : '',
        };
        setNoteFormErrors(errors);
        resolve(errors);
      } catch (error) {
        console.log(`Validation error`);
        reject(errors);
      }
    });
  }

  const handleNoteSubmit = async () => {
    setIsNoteLoading(true);
    const errors = await validateNote();
    const hasErrors = Object.values(errors).some((error) => error);
    if (!hasErrors) {
      // handle form submission 
      let payload = {
        caseRecordId,
        caseNoteId,
        note: notes
      };
      await axiosInstance.post('/Case/AddUpdateCaseRecordNote', payload).then(({ data, status }) => {
        setIsShowEdit(false);
        setIsNoteLoading(false);
        setNote({ ...note, note: notes });
      })
        .catch((error) => {
          setIsNoteLoading(false);
          console.log(error);
        });
    } else {
      setIsNoteLoading(false);
      console.log('No valid!');
    }
  };

  const handleDeleteNote = (caseNoteId: number, note: string) => {
    const options: SweetAlertOptions = {
      title: 'Are you sure you want to delete?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
    };
    Swal.fire(options).then((result) => {
      if (result.isConfirmed) {
        setIsNoteLoading(true);
        const payload = {
          note,
          caseRecordId,
          caseNoteId,
          isDeleted: true
        };
        axiosInstance.post('/Case/AddUpdateCaseRecordNote', payload).then(({ status }) => {
          if (status === 200) {
            toast.success('Note deleted successfully');
            window.location.reload();
          }
        });
      }
    });
  };

  return (
    <Dimmer.Dimmable as="div" dimmed={isNoteLoading}>
      <Dimmer active={isNoteLoading} inverted={true}>
        <Loader>Loading</Loader>
      </Dimmer>
      <List.Item key={noteIndex} style={{ padding: '1rem', marginBottom: '1rem' }}>
        <List.Content style={{ fontSize: '1.6rem' }}>
          <List.Header>
            <span className='note-header'>{note.createdBy}</span>
            <span className='history-time' style={{ float: 'right' }}>{moment(note.createdDate).format('MM/DD/YYYY')}</span>
            {note.createdById.toString() === userInfo?.UserId && (
              <>
                <span className='history-time' style={{ cursor: 'pointer', float: 'right', marginRight: '0.5rem' }} onClick={() => {
                  handleDeleteNote(note.caseNoteId, note.note)
                }}><Icon name='trash' /></span>
                <span className='history-time' style={{ cursor: 'pointer', float: 'right' }} onClick={() => {
                  setIsShowEdit(true);
                  setCaseNoteId(note.caseNoteId);
                  setNotes(note.note);
                }}><Icon name='pencil' /></span>
              </>
            )}
          </List.Header>
          <List.Description style={{ marginTop: '5px' }}>
            {isShowEdit && (
              <>
                <div className='field mt-2'>
                  <TextArea
                    style={{ minHeight: 100 }}
                    name="notes"
                    value={notes}
                    onChange={handleTextAreaChange}
                    maxLength={3000}
                  />
                </div>
                <Grid style={{ marginBottom: '1rem' }}>
                  <Grid.Column>
                    <Button type="submit" onClick={handleNoteSubmit} className='violet right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem' }}>
                      Save
                    </Button>
                    <Button type="button" className='second right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem', color: 'white' }} onClick={() => setIsShowEdit(false)}>
                      Cancel
                    </Button>
                  </Grid.Column>
                </Grid>
              </>
            )}
            {!isShowEdit && (
              <>{note.note}</>
            )}
          </List.Description>
        </List.Content>
      </List.Item>
    </Dimmer.Dimmable>
  );
};

export default CaseNote;